<template>
  <div
    v-if="shouldShowSelector"
    class="reserva-servicios-seleccion mb-20"
    :class="{ 'with-header': agrupacionServicios.servicios.length > 1 }">
    <div
      v-if="agrupacionServicios.servicios.length > 1"
      class="header-card mb-10 d-flex justify-content-between align-items-center"
      :class="{ 'container-imagen-fondo': imagenFondoCss, collapsed: headerCollapsed }"
      @click="toggleHeaderCollapse">
      <div class="header-content d-flex align-items-center">
        <img
          v-if="agrupacionServicios.icono"
          height="36"
          :src="agrupacionServicios.icono"
          :alt="agrupacionServicios.nombre"
          class="me-2 agrupacion-icon" />
        <CustomIcon v-else :icon="serviciosMetaData.icono" class="me-2 text-landing" size="36px" />
        <div class="text-info-container">
          <span class="text-landing semibold h6 translate">{{ agrupacionServicios.nombre }}</span>
          <InfoIcon
            v-if="hasNominalServicios"
            class="custom-svgInfo"
            size="12px"
            :message-info="t('servicios.tooltipMessageIsNominal')" />
        </div>
      </div>
      <CustomIcon :icon="headerCollapsed ? arrowDownIcon : arrowUpIcon" size="24px" class="text-landing" />
    </div>
    <div v-if="!headerCollapsed">
      <div :class="{ 'ml-10 mr-10': agrupacionServicios.servicios.length > 1 }">
        <div v-for="servicio in serviciosSinAgrupar" :key="servicio.id" class="mb-2 mb-mobile-5">
          <div
            class="servicio-card"
            :class="getServicioClasses(servicio)"
            :data-bs-toggle="disabledAllServicios ? 'tooltip' : null"
            :data-bs-title="disabledAllServicios ? $t('servicios.faltaFechaNacimiento') : null"
            :data-bs-placement="'top'"
            @click="selectServicio(servicio)">
            <div class="servicio-card-content servicio-main-content d-flex justify-content-between align-items-center">
              <div v-if="isAgrupacionUnica" class="servicio-agrupacion-icon flex-shrink-0 me-2">
                <img
                  v-if="agrupacionServicios.icono"
                  height="36"
                  :src="agrupacionServicios.icono"
                  :alt="agrupacionServicios.nombre"
                  class="agrupacion-icon" />
                <CustomIcon v-else :icon="serviciosMetaData.icono" class="text-landing" size="36px" />
              </div>
              <div class="servicio-content flex-grow-1">
                <div class="servicio-info-mobile servicio-info-container d-flex align-items-center flex-wrap">
                  <!-- Desktop -->
                  <div class="servicio-title-row d-md-flex d-none">
                    <span class="text-landing translate p" :class="{ semibold: isSelected(servicio) }">
                      {{ servicio.nombre }}
                    </span>
                    <span class="servicio-fechas small"> {{ formatServiceDateRange(servicio) }} </span>
                    <a
                      v-if="servicio.infoExtra || servicio.adjuntosPreventa.length > 0"
                      href="#"
                      class="info-extra-link small text-decoration-underline"
                      data-bs-toggle="modal"
                      :data-bs-target="`#infoExtraModal${servicio.id}`"
                      @click.stop>
                      + {{ $t('general.info') }}
                    </a>
                  </div>

                  <!-- Mobile -->
                  <div class="d-md-none w-100">
                    <div class="d-flex align-items-center gap-2 mb-1">
                      <span class="text-landing translate p" :class="{ semibold: isSelected(servicio) }">
                        {{ servicio.nombre }}
                      </span>
                      <a
                        v-if="servicio.infoExtra || servicio.adjuntosPreventa.length > 0"
                        href="#"
                        class="info-extra-link small text-decoration-underline"
                        data-bs-toggle="modal"
                        :data-bs-target="`#infoExtraModal${servicio.id}`"
                        @click.stop>
                        + {{ $t('general.info') }}
                      </a>
                    </div>

                    <div class="servicio-fechas small mb-1">{{ formatServiceDateRange(servicio) }}</div>
                  </div>
                </div>
                <!-- Mobile -->
                <div v-if="servicio.atributos" class="servicio-descripcion small d-md-none text-landing translate">
                  {{ getAtributosConcatenados(agrupacionServicios, servicio) }}
                </div>
                <div v-if="servicio.descripcion" class="servicio-descripcion small d-md-none text-landing translate">
                  {{ servicio.descripcion }}
                </div>

                <!-- Desktop -->
                <div
                  v-if="servicio.atributos"
                  class="servicio-descripcion small mt-1 d-none d-md-block text-landing translate">
                  {{ getAtributosConcatenados(agrupacionServicios, servicio) }}
                </div>
                <div
                  v-if="servicio.descripcion"
                  class="servicio-descripcion small mt-1 d-none d-md-block text-landing translate">
                  {{ servicio.descripcion }}
                </div>
              </div>

              <div class="servicio-right-section d-flex align-items-center">
                <!-- Desktop -->
                <div
                  v-if="isSelected(servicio) && !servicio.nominal && selectedServicioWithCantidad"
                  class="cantidad-control me-3 d-none d-md-block"
                  @click.stop>
                  <InputSelectorCantidad
                    :disabled="!isServicioContratable(reserva, selectedServicioWithCantidad.servicio)"
                    :count="selectedServicioWithCantidad.cantidad"
                    :min-count="
                      isServicioDescontratable(reserva, selectedServicioWithCantidad.servicio)
                        ? 1
                        : selectedServicioWithCantidad.cantidadOriginal
                    "
                    :max-count="calculaMaxCantidadForServicio(selectedServicioWithCantidad.servicio)"
                    :real-max-count="calculaMaxRealCantidadForServicio(selectedServicioWithCantidad.servicio)"
                    :is-agotado="!isSelectable(selectedServicioWithCantidad.servicio)"
                    @update-count="updateCantidad" />
                </div>

                <div class="servicio-precio-estado">
                  <template v-if="!isSelectable(servicio)">
                    <span class="badge bg-danger small">{{ $t('general.agotado') }}</span>
                  </template>
                  <template v-else>
                    <div class="precio-estado-container d-flex align-items-center">
                      <template v-if="!isInscripcion">
                        <span v-if="servicio.paqueteBasico && isSelected(servicio)" class="text-landing me-2 small">
                          {{ $t('general.incluido') }}
                        </span>
                        <span
                          v-else-if="isAgrupacionPaqueteBasico || servicio.precio !== 0"
                          class="text-landing p semibold me-2">
                          <span v-if="servicio.precio > 0">+</span>
                          <span>{{ servicio.precio }}{{ agenciaStore.getCurrency }}</span>
                        </span>
                      </template>
                      <div
                        v-if="isSelected(servicio)"
                        class="check-circle d-flex align-items-center justify-content-center">
                        <i class="fa-solid fa-check"></i>
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- Mobile -->
            <div
              v-if="isSelected(servicio) && !servicio.nominal && selectedServicioWithCantidad"
              class="cantidad-control-mobile cantidad-container-mobile d-block d-md-none d-flex justify-content-end"
              @click.stop>
              <InputSelectorCantidad
                :disabled="!isServicioContratable(reserva, selectedServicioWithCantidad.servicio)"
                :count="selectedServicioWithCantidad.cantidad"
                :min-count="
                  isServicioDescontratable(reserva, selectedServicioWithCantidad.servicio)
                    ? 1
                    : selectedServicioWithCantidad.cantidadOriginal
                "
                :max-count="calculaMaxCantidadForServicio(selectedServicioWithCantidad.servicio)"
                :real-max-count="calculaMaxRealCantidadForServicio(selectedServicioWithCantidad.servicio)"
                :is-agotado="!isSelectable(selectedServicioWithCantidad.servicio)"
                @update-count="updateCantidad" />
            </div>
          </div>
        </div>

        <div v-for="grupo in serviciosAgrupados" :key="grupo.id" class="grupo-firstfilter">
          <div v-if="grupo.name" class="grupo-titulo-header grupo-header mb-10" @click="toggleGrupoCollapse(grupo.id)">
            <div class="grupo-header-content d-flex align-items-center justify-content-between">
              <span class="text-landing semibold p">{{ grupo.name }}</span>
              <CustomIcon
                :icon="isGrupoCollapsed(grupo.id) ? arrowDownIcon : arrowUpIcon"
                size="24px"
                class="text-landing" />
            </div>
          </div>
          <template v-if="!isGrupoCollapsed(grupo.id)">
            <div v-for="servicio in grupo.servicios" :key="servicio.id" class="mb-2 mb-mobile-5">
              <div
                class="servicio-card"
                :class="getServicioClasses(servicio)"
                :data-bs-toggle="disabledAllServicios || isAgrupacionServiciosDisabled() ? 'tooltip' : null"
                :data-bs-title="disabledAllServicios ? $t('servicios.faltaFechaNacimiento') : null"
                :data-bs-placement="'top'"
                @click="selectServicio(servicio)">
                <div
                  class="servicio-card-content servicio-main-content d-flex justify-content-between align-items-center">
                  <div v-if="isAgrupacionUnica" class="servicio-agrupacion-icon flex-shrink-0 me-2">
                    <img
                      v-if="agrupacionServicios.icono"
                      height="36"
                      :src="agrupacionServicios.icono"
                      :alt="agrupacionServicios.nombre"
                      class="agrupacion-icon" />
                    <CustomIcon v-else :icon="serviciosMetaData.icono" class="text-landing" size="36px" />
                  </div>
                  <div class="servicio-content flex-grow-1">
                    <div class="servicio-info-mobile servicio-info-container d-flex align-items-center flex-wrap">
                      <!-- Desktop -->
                      <div class="servicio-title-row d-md-flex d-none">
                        <span class="text-landing translate p" :class="{ semibold: isSelected(servicio) }">
                          {{ servicio.nombre }}
                        </span>
                        <span class="servicio-fechas small"> {{ formatServiceDateRange(servicio) }} </span>
                        <a
                          v-if="servicio.infoExtra || servicio.adjuntosPreventa.length > 0"
                          href="#"
                          class="info-extra-link small text-decoration-underline"
                          data-bs-toggle="modal"
                          :data-bs-target="`#infoExtraModal${servicio.id}`"
                          @click.prevent.stop>
                          + {{ $t('general.info') }}
                        </a>
                      </div>

                      <!-- Mobile -->
                      <div class="d-md-none w-100">
                        <div class="d-flex align-items-center gap-2 mb-1">
                          <span class="text-landing translate p" :class="{ semibold: isSelected(servicio) }">
                            {{ servicio.nombre }}
                          </span>
                          <a
                            v-if="servicio.infoExtra || servicio.adjuntosPreventa.length > 0"
                            href="#"
                            class="info-extra-link small text-decoration-underline"
                            data-bs-toggle="modal"
                            :data-bs-target="`#infoExtraModal${servicio.id}`"
                            @click.prevent.stop>
                            + {{ $t('general.info') }}
                          </a>
                        </div>

                        <div class="servicio-fechas small mb-1">{{ formatServiceDateRange(servicio) }}</div>
                      </div>
                    </div>
                    <!-- Mobile -->
                    <div v-if="servicio.atributos" class="servicio-descripcion small d-md-none text-landing translate">
                      {{ getAtributosConcatenados(agrupacionServicios, servicio) }}
                    </div>
                    <div
                      v-if="servicio.descripcion"
                      class="servicio-descripcion small d-md-none text-landing translate">
                      {{ servicio.descripcion }}
                    </div>

                    <!-- Desktop -->
                    <div
                      v-if="servicio.atributos"
                      class="servicio-descripcion small mt-1 d-none d-md-block text-landing translate">
                      {{ getAtributosConcatenados(agrupacionServicios, servicio) }}
                    </div>
                    <div
                      v-if="servicio.descripcion"
                      class="servicio-descripcion small mt-1 d-none d-md-block text-landing translate">
                      {{ servicio.descripcion }}
                    </div>
                  </div>

                  <div class="servicio-right-section d-flex align-items-center">
                    <!-- Desktop -->
                    <div
                      v-if="isSelected(servicio) && !servicio.nominal && selectedServicioWithCantidad"
                      class="cantidad-control me-3 d-none d-md-block"
                      @click.stop>
                      <InputSelectorCantidad
                        :disabled="!isServicioContratable(reserva, selectedServicioWithCantidad.servicio)"
                        :count="selectedServicioWithCantidad.cantidad"
                        :min-count="
                          isServicioDescontratable(reserva, selectedServicioWithCantidad.servicio)
                            ? 1
                            : selectedServicioWithCantidad.cantidadOriginal
                        "
                        :max-count="calculaMaxCantidadForServicio(selectedServicioWithCantidad.servicio)"
                        :real-max-count="calculaMaxRealCantidadForServicio(selectedServicioWithCantidad.servicio)"
                        :is-agotado="!isSelectable(selectedServicioWithCantidad.servicio)"
                        @update-count="updateCantidad" />
                    </div>

                    <div class="servicio-precio-estado">
                      <template v-if="!isSelectable(servicio)">
                        <span class="badge bg-danger small">{{ $t('general.agotado') }}</span>
                      </template>
                      <template v-else>
                        <div class="precio-estado-container d-flex align-items-center">
                          <template v-if="!isInscripcion">
                            <span v-if="servicio.paqueteBasico && isSelected(servicio)" class="text-landing me-2 small">
                              {{ $t('general.incluido') }}
                            </span>
                            <span
                              v-else-if="isAgrupacionPaqueteBasico || servicio.precio !== 0"
                              class="text-landing p semibold me-2">
                              <template v-if="servicio.precio > 0">+</template>
                              <span>{{ servicio.precio }}{{ agenciaStore.getCurrency }}</span>
                            </span>
                          </template>
                          <div
                            v-if="isSelected(servicio)"
                            class="check-circle d-flex align-items-center justify-content-center">
                            <i class="fa-solid fa-check"></i>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>

                <!-- Mobile -->
                <div
                  v-if="isSelected(servicio) && !servicio.nominal && selectedServicioWithCantidad"
                  class="cantidad-control-mobile cantidad-container-mobile d-block d-md-none d-flex justify-content-end"
                  @click.stop>
                  <InputSelectorCantidad
                    :disabled="!isServicioContratable(reserva, selectedServicioWithCantidad.servicio)"
                    :count="selectedServicioWithCantidad.cantidad"
                    :min-count="
                      isServicioDescontratable(reserva, selectedServicioWithCantidad.servicio)
                        ? 1
                        : selectedServicioWithCantidad.cantidadOriginal
                    "
                    :max-count="calculaMaxCantidadForServicio(selectedServicioWithCantidad.servicio)"
                    :real-max-count="calculaMaxRealCantidadForServicio(selectedServicioWithCantidad.servicio)"
                    :is-agotado="!isSelectable(selectedServicioWithCantidad.servicio)"
                    @update-count="updateCantidad" />
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <template v-for="servicio in availableServicios" :key="`modal-${servicio.id}`">
      <InfoExtraModal
        v-if="servicio.infoExtra || servicio.adjuntosPreventa.length > 0"
        :modal-id="`infoExtraModal${servicio.id}`"
        :title="servicio.nombre"
        :content="servicio.infoExtra"
        :adjuntos="servicio.adjuntosPreventa" />
    </template>
  </div>
</template>

<script setup>
  import { ref, computed, onBeforeMount, onMounted, reactive, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { informacionHabitacion } from '@/store_pinia/informacionHabitacion';
  import { agenciaStoreModule } from '@/store_pinia/agencia';
  import {
    getAtributosConcatenados,
    getCategoriasServiciosMetaData,
    isServicioContratable,
    isServicioDescontratable,
    isServicioValidoEdad,
  } from '@/helpers/serviciosUtils';
  import { formatDateRangeDDmm, calcularEdad } from '@/helpers/dateUtiles';
  import { toRawDeep } from '@/helpers/reactivityUtils';
  import InfoIcon from '@/components/Icons/InfoIcon.vue';
  import InputSelectorCantidad from '@/components/inputs/InputSelectorCantidad';
  import CustomIcon from '@/components/CustomIcon.vue';
  import InfoExtraModal from '@/components/modales/InfoExtraModal.vue';
  import arrowDownIcon from '@/assets/icons/arrow_down.svg';
  import arrowUpIcon from '@/assets/icons/arrow_up.svg';
  import { Tooltip } from 'bootstrap';
  import { ESTADOS_INVITACION_GRUPO, TIPOS_PAGO } from '@/constants/viajeConstants';

  const props = defineProps({
    agrupacionServicios: { type: Object, default: null },
    idxReserva: { type: Number, default: null },
    disabledAllServicios: { type: Boolean, default: false },
  });

  const emits = defineEmits(['servicioSeleccionado']);

  const { t } = useI18n();
  const storeHabitacion = informacionHabitacion();
  const agenciaStore = agenciaStoreModule();

  const selectedServicioWithCantidad = ref(null);
  const collapsedGrupos = reactive({});
  const headerCollapsed = ref(false);
  const imagenFondoCss = computed(() =>
    props.agrupacionServicios.imagenFondo ? `url(${props.agrupacionServicios.imagenFondo})` : ''
  );

  const viaje = computed(() => storeHabitacion.getViaje);
  const reserva = computed(() => storeHabitacion.getReservaByIdx(props.idxReserva));
  const hasNominalServicios = computed(() => props.agrupacionServicios.servicios.some(s => s.nominal));
  const servicioPaqueteBasico = computed(() => props.agrupacionServicios.servicios.find(s => s.paqueteBasico));
  const isAgrupacionPaqueteBasico = computed(() => props.agrupacionServicios.paqueteBasico);
  const isInscripcion = computed(
    () =>
      viaje.value.tipoPago === TIPOS_PAGO.SIN_PAGO ||
      viaje.value.invitacionGrupo?.estado === ESTADOS_INVITACION_GRUPO.ACEPTADA
  );

  const serviciosMetaData = computed(() =>
    getCategoriasServiciosMetaData().find(({ selector }) => selector === props.agrupacionServicios.tipo)
  );

  const isAgrupacionUnica = computed(() => props.agrupacionServicios.servicios.length === 1);

  const edadUsuario = computed(() => {
    const fechaNacimiento = reserva.value?.usuario?.fechaNacimiento;
    return fechaNacimiento ? calcularEdad(fechaNacimiento) : null;
  });

  const hasAgrupacionTarifaEdad = computed(() => {
    return props.agrupacionServicios.servicios.some(s => s.tarifaEdad);
  });

  const isAgrupacionDisabled = computed(() => {
    if (!hasAgrupacionTarifaEdad.value) return false;
    if (edadUsuario.value === null) return true;

    return !props.agrupacionServicios.servicios.some(servicio => {
      if (!servicio.tarifaEdad) return false;
      return isServicioValidoEdad(servicio, edadUsuario.value);
    });
  });

  const availableServicios = computed(() => {
    const servicios = props.agrupacionServicios.servicios.filter(s => s.activo || isSelected(s));
    return servicios.sort((a, b) => a.precio - b.precio);
  });

  const serviciosAgrupados = computed(() => {
    const serviciosConFiltros = availableServicios.value.filter(s =>
      s.atributos?.some(a => a.atributo.nombre === props.agrupacionServicios.atributo?.nombre)
    );
    if (serviciosConFiltros.length === 0) {
      return [];
    }

    const grupos = {};
    serviciosConFiltros.forEach(servicio => {
      const atributo = servicio.atributos.find(a => a.atributo.nombre === props.agrupacionServicios.atributo.nombre);
      const key = atributo.valor.id;
      const name = atributo.valor.nombre;
      if (!grupos[name]) {
        grupos[name] = {
          id: key,
          name: t(`servicios.valoresAtributos.${servicio.categoria?.nombre}.${atributo.atributo.nombre}.${name}`, name),
          servicios: [],
        };
      }
      grupos[name].servicios.push(servicio);
    });

    return Object.values(grupos);
  });

  const serviciosSinAgrupar = computed(() => {
    return availableServicios.value.filter(
      s => !s.atributos?.some(a => a.atributo.nombre === props.agrupacionServicios.atributo?.nombre)
    );
  });

  const shouldShowSelector = computed(() => {
    const selectedServicio = selectedServicioWithCantidad.value?.servicio || null;
    const serviciosSelectables = props.agrupacionServicios.servicios.filter(
      s => s.activo || selectedServicio?.id === s.id
    );
    if (serviciosSelectables.length === 1) {
      const servicio = serviciosSelectables[0];
      if (servicio.paqueteBasico && servicio.obligatorio && servicio.nominal) {
        return false;
      }
    }
    return selectedServicio || serviciosSelectables.length > 0;
  });

  function isSelected(servicio) {
    return selectedServicioWithCantidad.value?.servicio?.id === servicio.id;
  }

  function isSelectable(servicio) {
    return (
      isServicioContratable(reserva.value, servicio) &&
      (isSelected(servicio) || calculaMaxCantidadForServicio(servicio) > 0)
    );
  }

  function isAgrupacionServiciosDisabled() {
    return props.disabledAllServicios || isAgrupacionDisabled.value;
  }

  function formatServiceDateRange(servicio) {
    if (!servicio.fechaSelected) return '';
    return formatDateRangeDDmm(servicio?.fechaInicio, servicio?.fechaFin, {
      rangeFormatter: (from, to) => `(${t('general.del')} ${from} ${t('general.al')} ${to})`,
    });
  }

  function getServicioClasses(servicio) {
    const disabledPorEdad = isAgrupacionServiciosDisabled();

    return {
      selected: isSelected(servicio),
      disabled: (!isSelectable(servicio) && !isSelected(servicio)) || disabledPorEdad,
      agotado: !isSelectable(servicio),
      'no-deselectable': isSelected(servicio) && (servicio.paqueteBasico || servicio.obligatorio),
    };
  }

  function toggleHeaderCollapse() {
    headerCollapsed.value = !headerCollapsed.value;
  }

  function toggleGrupoCollapse(grupoId) {
    collapsedGrupos[grupoId] = !collapsedGrupos[grupoId];
  }

  function isGrupoCollapsed(grupoId) {
    return collapsedGrupos[grupoId];
  }

  function calculaMaxRealCantidadForServicio(servicio) {
    let cantidadActual = 0;
    if (selectedServicioWithCantidad.value && selectedServicioWithCantidad.value.servicio) {
      if (servicio.cuposMetasId?.some(id => selectedServicioWithCantidad.value.servicio.cuposMetasId?.includes(id))) {
        cantidadActual = selectedServicioWithCantidad.value.cantidad;
      }
    }
    return cantidadActual + servicio.servicioOriginal.numCupos;
  }

  function calculaMaxCantidadForServicio(servicio) {
    return Math.min(calculaMaxRealCantidadForServicio(servicio), 200);
  }

  function selectServicio(servicio) {
    if (!isSelectable(servicio) || isAgrupacionServiciosDisabled(servicio)) return;

    if (isSelected(servicio)) {
      if (servicio.paqueteBasico) return;

      if (servicioPaqueteBasico.value) {
        updateSelectedServicio(servicioPaqueteBasico.value);
      } else {
        deselectAll();
      }
      return;
    }

    updateSelectedServicio(servicio);
  }

  function updateSelectedServicio(servicio) {
    const servicioToSelect = {
      ...toRawDeep(servicio),
      servicioOriginal: servicio.servicioOriginal,
      justSelected: true,
    };

    const oldServicio = selectedServicioWithCantidad.value?.servicio;
    const oldCantidad = selectedServicioWithCantidad.value?.cantidad;

    selectedServicioWithCantidad.value = {
      servicio: servicioToSelect,
      oldServicio: oldServicio,
      cantidad: 1,
      oldCantidad: 1,
      cantidadOriginal: 1,
      categoriaPaqueteBasico: isAgrupacionPaqueteBasico.value,
      servicioPB: servicioPaqueteBasico.value,
    };

    saveToStore();

    storeHabitacion.decrementCupoServicios(servicioToSelect);
    if (oldServicio) {
      storeHabitacion.incrementCupoServicios(oldServicio, oldCantidad);
    }
  }

  function deselectAll() {
    const servicio = selectedServicioWithCantidad.value?.servicio;
    const cantidad = selectedServicioWithCantidad.value?.cantidad;

    if (servicio) {
      storeHabitacion.incrementCupoServicios(servicio, cantidad);
    }
    selectedServicioWithCantidad.value = null;

    if (reserva.value?.serviciosWithCantidad) {
      delete reserva.value.serviciosWithCantidad[props.agrupacionServicios.id];
      emits('servicioSeleccionado', props.idxReserva, props.agrupacionServicios.id, null);
    }
  }

  function updateCantidad(newCantidad) {
    if (!selectedServicioWithCantidad.value) return;

    selectedServicioWithCantidad.value.cantidad = newCantidad;
    saveToStore();

    if (selectedServicioWithCantidad.value.cantidad < selectedServicioWithCantidad.value.oldCantidad) {
      storeHabitacion.incrementCupoServicios(selectedServicioWithCantidad.value.servicio);
    } else {
      storeHabitacion.decrementCupoServicios(selectedServicioWithCantidad.value.servicio);
    }
    selectedServicioWithCantidad.value.oldCantidad = newCantidad;
  }

  function saveToStore() {
    if (!selectedServicioWithCantidad.value) return;

    storeHabitacion.addServicioToReserva(
      props.idxReserva,
      selectedServicioWithCantidad.value.servicio,
      selectedServicioWithCantidad.value.cantidad
    );
    emits('servicioSeleccionado', selectedServicioWithCantidad.value);
  }

  function getServicioForAutoselect() {
    if (!isAgrupacionPaqueteBasico.value || !servicioPaqueteBasico.value) {
      return null;
    }

    if (!hasAgrupacionTarifaEdad.value) {
      return servicioPaqueteBasico.value.servicioOriginal.numCupos > 0 ? servicioPaqueteBasico.value : null;
    }

    if (servicioPaqueteBasico.value.servicioOriginal.numCupos > 0) {
      return servicioPaqueteBasico.value;
    }

    const alternativaValida = props.agrupacionServicios.servicios
      .filter(s => s.activo && s.servicioOriginal.numCupos > 0)
      .sort((a, b) => a.precio - b.precio)[0];

    return alternativaValida || null;
  }

  function init() {
    const existingServicioWithCantidad = reserva.value?.serviciosWithCantidad[props.agrupacionServicios.id];
    // If the booking does not have any service of this category needs to be initialised
    if (!existingServicioWithCantidad) {
      let servicio = getServicioForAutoselect();

      if (servicio) {
        selectedServicioWithCantidad.value = {
          servicio: servicio,
          oldServicio: servicio,
          cantidad: 1,
          oldCantidad: 1,
          cantidadOriginal: 1,
          categoriaPaqueteBasico: isAgrupacionPaqueteBasico.value,
          servicioPB: servicioPaqueteBasico.value,
        };
        storeHabitacion.decrementCupoServicios(servicio);
        saveToStore();
      }
    } else {
      selectedServicioWithCantidad.value = {
        servicio: existingServicioWithCantidad.servicio,
        oldServicio: existingServicioWithCantidad.servicio,
        cantidad: existingServicioWithCantidad.cantidad,
        oldCantidad: existingServicioWithCantidad.cantidad,
        cantidadOriginal: existingServicioWithCantidad.cantidad,
        categoriaPaqueteBasico: isAgrupacionPaqueteBasico.value,
        servicioPB: servicioPaqueteBasico.value,
      };
      saveToStore();
    }
  }

  onBeforeMount(() => {
    init();
  });

  watch(edadUsuario, (newEdad, oldEdad) => {
    const currentServicio = selectedServicioWithCantidad.value?.servicio;
    if (newEdad === oldEdad || !currentServicio || (!currentServicio.tarifaEdad && !hasAgrupacionTarifaEdad.value)) {
      return;
    }
    if (isAgrupacionPaqueteBasico.value) {
      const servicioParaEdad = getServicioForAutoselect();
      if (servicioParaEdad) {
        updateSelectedServicio(servicioParaEdad);
      }
    }
  });

  function initTooltips() {
    const tooltipTriggerList = document.querySelectorAll('.servicio-card[data-bs-toggle="tooltip"]');
    [...tooltipTriggerList].forEach(tooltipTriggerEl => {
      new Tooltip(tooltipTriggerEl, {
        trigger: 'hover',
        container: tooltipTriggerEl.closest('.landing-experiencia') || false,
      });
    });
  }

  function destroyAllTooltips() {
    const allCards = document.querySelectorAll('.servicio-card');
    [...allCards].forEach(card => {
      const tooltip = Tooltip.getInstance(card);
      if (tooltip) {
        tooltip.dispose();
      }
    });
  }

  onMounted(() => {
    initTooltips();
  });

  watch(
    () => props.disabledAllServicios,
    newValue => {
      destroyAllTooltips();
      if (newValue) {
        initTooltips();
      }
    }
  );
</script>

<style lang="scss" scoped>
  .reserva-servicios-seleccion {
    &:last-child {
      margin-bottom: 0 !important;
    }
    &.with-header {
      border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.2);
      border-radius: var(--bs-border-radius-lg);
      background-color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.05);
    }

    .header-card {
      background-color: rgba(var(--landing-text-color-rgb, var(--bs-light-rgb)), 0.1);
      border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1);
      padding: 16px 20px;
      border-top-left-radius: var(--bs-border-radius-lg);
      border-top-right-radius: var(--bs-border-radius-lg);
      cursor: pointer;
      transition: all 0.2s ease;

      &.collapsed {
        border-bottom-left-radius: var(--bs-border-radius-lg);
        border-bottom-right-radius: var(--bs-border-radius-lg);
        margin-bottom: 0 !important;
      }

      &:hover {
        background-color: rgba(var(--landing-text-color-rgb, var(--bs-light-rgb)), 0.15);
      }
    }

    .text-info-container {
      min-width: 0;
    }

    .container-imagen-fondo {
      border: none;
      background-image: linear-gradient(
          color-mix(in srgb, white 70%, transparent),
          color-mix(in srgb, white 70%, transparent)
        ),
        v-bind('imagenFondoCss');
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
    }

    .grupo-firstfilter {
      .grupo-titulo-header {
        background-color: rgba(var(--landing-text-color-rgb, var(--bs-light-rgb)), 0.1);
        border-radius: var(--bs-border-radius-lg);
        padding: 12.5px 15px;
        cursor: pointer;
        transition: all 0.2s ease;
        margin-bottom: 10px;
        border: 1px solid rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.1);

        &:hover {
          background-color: rgba(var(--landing-text-color-rgb, var(--bs-light-rgb)), 0.15);
        }
      }
    }

    .custom-svgInfo {
      margin-left: 0.3rem;
    }

    .agrupacion-icon {
      width: 36px;
      height: 36px;
    }

    .servicio-card {
      padding: 15px;
      min-height: 63px;
      border: 1px solid rgba(var(--landing-text-color-rgb, 224, 224, 224), 0.3);
      border-radius: var(--bs-border-radius);
      cursor: pointer;
      transition: all 0.2s ease;
      background-color: var(--landing-background-color, white);

      .servicio-info-container {
        .servicio-title-row {
          gap: 10px;
        }
      }

      &:hover:not(.disabled) {
        border-color: var(--landing-detail-color, var(--bs-primary));
        box-shadow: 0 2px 4px rgba(var(--landing-detail-color-rgb, var(--bs-primary-rgb)), 0.1);
      }

      &.selected {
        border-color: var(--landing-detail-color, var(--bs-secondary));
        background-color: rgba(var(--landing-detail-color-rgb, var(--bs-secondary-rgb)), 0.1);
        border-width: 2px;
      }

      &.disabled {
        cursor: not-allowed;
        opacity: 0.6;
        background-color: var(--landing-background-color, #f8f9fa);
      }

      &.agotado {
        border-color: var(--bs-danger-rgb);
        background-color: rgba(var(--bs-danger-rgb), 0.05);
      }

      .servicio-content {
        .servicio-descripcion {
          line-height: 1.3;
        }
      }

      .cantidad-control {
        position: relative;
        z-index: 10;
      }

      .servicio-precio-estado {
        .check-circle {
          width: 15px;
          height: 15px;
          border-radius: 50%;
          background-color: var(--landing-detail-color, var(--bs-secondary));

          i {
            color: var(--bs-white);
            font-size: 8px;
          }
        }
      }
    }

    .servicio-fechas {
      color: rgba(var(--landing-text-color-rgb, var(--bs-primary-rgb)), 0.8);
      line-height: unset !important;
    }

    .info-extra-link {
      color: var(--landing-text-color, var(--bs-primary)) !important;
      line-height: unset !important;

      &:hover {
        color: var(--landing-text-color, var(--bs-primary)) !important;
        opacity: 0.8;
      }
    }
  }

  @media (max-width: 768px) {
    .reserva-servicios-seleccion {
      .mb-mobile-5 {
        margin-bottom: 5px !important;
      }

      .agrupacion-icon {
        width: 20px !important;
        height: 20px !important;
      }

      .servicio-card {
        padding: 20px 15px;
        .servicio-content {
          margin-right: 15px;
        }

        .cantidad-container-mobile {
          position: relative;
          z-index: 10;
        }
      }
    }
  }
</style>

<style lang="scss">
  .tooltip {
    .tooltip-inner {
      background-color: var(--landing-detail-color, var(--bs-primary)) !important;
      color: var(--bs-white) !important;
      white-space: nowrap !important;
      max-width: none !important;
    }

    .tooltip-arrow::before {
      border-top-color: var(--landing-detail-color, var(--bs-primary)) !important;
    }

    &.bs-tooltip-bottom .tooltip-arrow::before {
      border-bottom-color: var(--landing-detail-color, var(--bs-primary)) !important;
    }

    &.bs-tooltip-start .tooltip-arrow::before {
      border-left-color: var(--landing-detail-color, var(--bs-primary)) !important;
    }

    &.bs-tooltip-end .tooltip-arrow::before {
      border-right-color: var(--landing-detail-color, var(--bs-primary)) !important;
    }
  }
</style>
